<template>
	<div class="pagination-container">
		<div class="pagination-controls">
			<button
				class="arrow-button"
				@click="prevPage"
				:disabled="isOnFirstPage"
			>
				<svg
					width="24"
					height="14"
					viewBox="0 0 24 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M22.75 6.75H0.75M0.75 6.75L6.75 0.75M0.75 6.75L6.75 12.75"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</button>

			<div class="page-numbers">
				<span
					@click="goToPage(1)"
					:class="{ active: currentPage === 1 }"
					class="page-number"
					>1</span
				>
				<span v-if="startPage > 2" class="dots">...</span>
				<span
					v-for="page in displayedPages"
					:key="page"
					@click="goToPage(page)"
					:class="{ active: currentPage === page }"
					class="page-number"
				>
					{{ page }}
				</span>
				<span v-if="endPage < totalPages - 1" class="dots">...</span>
				<span
					v-if="totalPages > 1"
					@click="goToPage(totalPages)"
					:class="{ active: currentPage === totalPages }"
					class="page-number"
				>
					{{ totalPages }}
				</span>
			</div>

			<button
				class="arrow-button"
				@click="nextPage"
				:disabled="isOnLastPage"
			>
				<svg
					width="24"
					height="14"
					viewBox="0 0 24 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M0.75 6.75H22.75M22.75 6.75L16.75 0.75M22.75 6.75L16.75 12.75"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</button>
		</div>
	</div>
</template>

<script>
import { onClickOutside } from '@vueuse/core';

export default {
	props: {
		totalPages: {
			type: Number,
			required: true,
		},
		perPage: {
			type: Number,
			default: 20,
		},
		currentPage: {
			type: Number,
			default: 1,
		},
	},
	emits: ['update:perPage', 'update:currentPage'],
	data() {
		return {
			dropdownVisible: false,
			itemsPerPageOptions: [20, 40, 60],
		};
	},
	computed: {
		startPage() {
			if (this.currentPage <= 3) return 2;
			if (this.currentPage >= this.totalPages - 2)
				return this.totalPages - 3;
			return this.currentPage - 1;
		},
		endPage() {
			if (this.currentPage <= 3) return Math.min(5, this.totalPages - 1);
			if (this.currentPage >= this.totalPages - 2)
				return this.totalPages - 1;
			return this.currentPage + 1;
		},
		displayedPages() {
			const pages = [];
			for (let i = this.startPage; i <= this.endPage; i++) {
				if (i !== 1 && i !== this.totalPages) {
					pages.push(i);
				}
			}
			return pages;
		},
		isOnFirstPage() {
			return this.currentPage === 1;
		},
		isOnLastPage() {
			return this.currentPage === this.totalPages;
		},
	},
	mounted() {
		onClickOutside(this.$refs.dropdownRef, () => {
			this.dropdownVisible = false;
		});
	},
	methods: {
		toggleDropdown() {
			this.dropdownVisible = !this.dropdownVisible;
		},
		changeItemsPerPage(newPerPage) {
			this.$emit('update:perPage', newPerPage);
			this.dropdownVisible = false;
		},
		goToPage(page) {
			if (page !== this.currentPage) {
				this.$emit('update:currentPage', page);
			}
		},
		prevPage() {
			if (!this.isOnFirstPage) {
				this.goToPage(this.currentPage - 1);
			}
		},
		nextPage() {
			if (!this.isOnLastPage) {
				this.goToPage(this.currentPage + 1);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/media' as *;

.pagination-container {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: auto;
	flex-wrap: wrap;
}

.arrow-down {
	transition: all 0.3s ease;
}

.arrow-down.rotated {
	transform: rotate(180deg);
}

.pagination-controls {
	display: flex;
	align-items: center;
	@media (max-width: $md5) {
		flex-wrap: wrap;
		justify-content: center;
		column-gap: 10px;
	}
}

.arrow-button {
	background: none;
	border: none;
	cursor: pointer;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	border-radius: 50%;
	background-color: var(--hint-color);
	transition: all 0.3s ease 0s;
	opacity: 1;
	pointer-events: auto;
	svg {
		position: absolute;
		width: 22px;
		height: 22px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		path {
			stroke: var(--primary-color);
			stroke-width: 2px;
		}
	}
	@media (any-hover: hover) {
		&:hover {
			background-color: var(--hint-hover-color);
		}
	}
	@media (max-width: $md5) {
	}
}

.arrow-button:disabled {
	pointer-events: none;
	opacity: 0.5;
}

.page-numbers {
	display: flex;
	align-items: center;
	margin: 0 8px;
	padding: 6px 8px;
	border-radius: 6px;
	@media (max-width: $md5) {
		order: 3;
		flex: 1 1 100%;
		justify-content: center;
	}
}

.page-number {
	min-width: 27px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: var(--font-size-base);
	font-weight: 400;
	color: var(--primary-color);

	&:not(:first-child, :last-child) {
		margin: 0 2px;
	}
}

.page-number.active {
	cursor: default;
	color: var(--hint-color);
}

.dots {
	color: var(--primary-color);
	cursor: default;
	margin: 0 10px 0px 2px;
}
</style>
